import { api } from './api';
import { ApiResponse, PaginatedResponse } from '@/types/api.types';

// Procedure Type interfaces
export interface ProcedureType {
  id: number;
  name_ro: string;
  name_en: string;
  created_at?: string;
  usage_count?: number;
}

export interface ProcedureTypeData {
  name_ro: string;
  name_en: string;
}

export interface ProcedureTypeFilters {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Cache for reference data
let procedureTypesCache: ProcedureType[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class ProcedureTypeService {
  
  // Admin endpoints
  async listProcedureTypes(filters: ProcedureTypeFilters = {}): Promise<PaginatedResponse<ProcedureType>> {
    const queryParams = new URLSearchParams();
    
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const url = `/api/admin/procedure-types${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await api.get<any>(url);
    
    // Map the backend response to PaginatedResponse structure
    return {
      data: response.data || [],
      total: response.pagination?.total || 0,
      page: response.pagination?.page || 1,
      limit: response.pagination?.limit || 20,
      pages: response.pagination?.pages || 1
    };
  }

  async getProcedureType(id: number): Promise<ProcedureType> {
    const response = await api.get<any>(`/api/admin/procedure-types/${id}`);
    return response.data;
  }

  async createProcedureType(data: ProcedureTypeData): Promise<ProcedureType> {
    // Validate data
    if (!data.name_ro?.trim()) {
      throw new Error('Numele în română este obligatoriu');
    }
    if (!data.name_en?.trim()) {
      throw new Error('Numele în engleză este obligatoriu');
    }

    const response = await api.post<any>('/api/admin/procedure-types', {
      name_ro: data.name_ro.trim(),
      name_en: data.name_en.trim()
    });

    // Clear cache after creation
    this.clearCache();
    
    return response.data;
  }

  async updateProcedureType(id: number, data: ProcedureTypeData): Promise<ProcedureType> {
    // Validate data
    if (!data.name_ro?.trim()) {
      throw new Error('Numele în română este obligatoriu');
    }
    if (!data.name_en?.trim()) {
      throw new Error('Numele în engleză este obligatoriu');
    }

    const response = await api.put<any>(`/api/admin/procedure-types/${id}`, {
      name_ro: data.name_ro.trim(),
      name_en: data.name_en.trim()
    });

    // Clear cache after update
    this.clearCache();
    
    return response.data;
  }

  async deleteProcedureType(id: number): Promise<void> {
    await api.delete(`/api/admin/procedure-types/${id}`);
    // Clear cache after deletion
    this.clearCache();
  }

  // Reference endpoint with caching
  async getActiveProcedureTypes(): Promise<ProcedureType[]> {
    const now = Date.now();
    
    // Check cache
    if (procedureTypesCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
      return procedureTypesCache;
    }

    try {
      const response = await api.get<any>('/api/reference/procedure-types');
      
      // Update cache
      procedureTypesCache = response.data || [];
      cacheTimestamp = now;
      
      return procedureTypesCache || [];
    } catch (error) {
      // If API call fails, return empty array or cached data if available
      console.error('Failed to load procedure types:', error);
      return procedureTypesCache || [];
    }
  }

  // Force refresh cache
  async refreshCache(): Promise<ProcedureType[]> {
    this.clearCache();
    return this.getActiveProcedureTypes();
  }

  // Clear cache
  clearCache(): void {
    procedureTypesCache = null;
    cacheTimestamp = null;
  }

  // Helper method to get procedure type label by ID
  async getProcedureTypeLabel(id: number): Promise<string> {
    try {
      const types = await this.getActiveProcedureTypes();
      const type = types.find(t => t.id === id);
      return type ? type.name_ro : 'Necunoscut';
    } catch (error) {
      console.error('Failed to get procedure type label:', error);
      return 'Necunoscut';
    }
  }
}

export const procedureTypeService = new ProcedureTypeService();